import React, { useEffect, useMemo, useState } from "react";

import { getUserDisplayName, getUserInitials } from "../utils/userProfile";
import { useAppI18n } from "../hooks/useAppI18n";
import { useSetAtom } from "../app-jotai";
import { appLangCodeAtom } from "../app-language/language-state";
import { useMicMode } from "../hooks/useMicMode";
import { useSubscription } from "../hooks/useSubscription";
import { ensureXiaolaiFont } from "../utils/ensureXiaolaiFont";

import { getBackendUrl } from "../utils/backendUrl";
import "./UserAuthModal.scss";

import type { Session } from "@supabase/supabase-js";
import type { UserProfile } from "../hooks/useSupabaseAuth";

interface UserAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
  onClearError: () => void;
  onGoogleSignIn: () => Promise<void>;
  onSendOtp: (email: string) => Promise<boolean>;
  onVerifyOtp: (email: string, token: string) => Promise<boolean>;
  onProfileUpdate: (updates: {
    full_name?: string;
    avatar_url?: string;
  }) => Promise<void>;
  onSignOut: () => Promise<void>;
}

export const UserAuthModal: React.FC<UserAuthModalProps> = ({
  isOpen,
  onClose,
  session,
  profile,
  loading,
  profileLoading,
  error,
  onClearError,
  onGoogleSignIn,
  onSendOtp,
  onVerifyOtp,
  onProfileUpdate,
  onSignOut,
}) => {
  const { t, langCode } = useAppI18n();
  const setLangCode = useSetAtom(appLangCodeAtom);
  const [micMode, setMicMode] = useMicMode();
  const userId = session?.user?.id ?? null;
  const { plan: userPlan, loading: subLoading } = useSubscription(userId);
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);

  // Ensure Xiaolai (CJK handwriting font) is registered
  ensureXiaolaiFont();

  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  /** "email" = enter email step, "otp" = enter 6-digit code step */
  const [step, setStep] = useState<"email" | "otp">("email");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const isAuthenticated = Boolean(session?.user);
  const isChinese = langCode.startsWith("zh");
  const displayName = useMemo(
    () => getUserDisplayName(profile, session?.user ?? null),
    [profile, session?.user],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setEmail("");
    setOtpCode("");
    setFullName(profile?.full_name ?? "");
    setShowSignOutConfirm(false);
    setStep("email");
    setSending(false);
    setVerifying(false);
  }, [isOpen, profile?.full_name]);

  const handleSendOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) {
      return;
    }
    setSending(true);
    const success = await onSendOtp(email);
    setSending(false);
    if (success) {
      setStep("otp");
    }
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      return;
    }
    setVerifying(true);
    await onVerifyOtp(email, otpCode);
    setVerifying(false);
    // If verification succeeds, onAuthStateChange fires and
    // the modal will automatically switch to the profile view.
  };

  const handleProfileSave = async () => {
    setSaving(true);
    await onProfileUpdate({
      full_name: fullName.trim() || undefined,
    });
    setSaving(false);
  };

  const handleUpgradeToPro = async () => {
    if (!userId) {
      return;
    }
    const base = getBackendUrl();
    setIsUpgradeLoading(true);
    try {
      const res = await fetch(`${base}/v1/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          price_id: "price_1T7PvlKBCcpgOgYlpWbcBBCe",
          user_id: userId,
          success_url: window.location.origin,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data.detail || data.message || `Request failed (${res.status})`;
        throw new Error(msg);
      }
      const checkout_url = data.checkout_url;
      if (!checkout_url) {
        throw new Error("No checkout URL returned");
      }
      window.location.href = checkout_url;
    } catch (e) {
      setIsUpgradeLoading(false);
      const message = e instanceof Error ? e.message : "Checkout failed";
      alert(message);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="ideaweave-auth-modal" role="presentation">
      <div className="ideaweave-auth-modal__backdrop" onClick={onClose} />
      <div
        className="ideaweave-auth-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("entry.userLogin")}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Decorative tape strip */}
        <div className="ideaweave-auth-modal__tape" aria-hidden="true" />

        <button
          type="button"
          className="ideaweave-auth-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <span className="material-symbols-outlined">close</span>
        </button>

        {loading ? (
          <div className="ideaweave-auth-modal__loading">
            {t("auth.loadingAccount")}
          </div>
        ) : isAuthenticated ? (
          <div className="ideaweave-auth-modal__profile">
            {/* Centered avatar + identity */}
            <div className="ideaweave-auth-modal__identity">
              <div className="ideaweave-auth-modal__avatar">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={displayName}
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = "none";
                      const span = document.createElement("span");
                      span.textContent = getUserInitials(displayName);
                      target.parentElement?.appendChild(span);
                    }}
                  />
                ) : (
                  <span>{getUserInitials(displayName)}</span>
                )}
              </div>
              <div className="ideaweave-auth-modal__name">{displayName}</div>
              <div className="ideaweave-auth-modal__email">
                {session?.user?.email ?? t("auth.noEmail")}
              </div>
            </div>

            {/* Dashed separator */}
            <div
              className="ideaweave-auth-modal__separator"
              aria-hidden="true"
            />

            {/* Name + Plan — two-column row */}
            <div className="ideaweave-auth-modal__name-plan-row">
              {/* Left: Name */}
              <div className="ideaweave-auth-modal__field ideaweave-auth-modal__field--name">
                <label htmlFor="iw-display-name">{t("auth.nameLabel")}</label>
                <div className="ideaweave-auth-modal__name-input-wrap">
                  <input
                    id="iw-display-name"
                    type="text"
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    placeholder={t("auth.namePlaceholder")}
                  />
                  <button
                    type="button"
                    className="ideaweave-auth-modal__save-icon-btn"
                    onClick={handleProfileSave}
                    disabled={saving || profileLoading}
                    aria-label={t("auth.save")}
                  >
                    <span className="material-symbols-outlined">
                      {saving ? "progress_activity" : "check"}
                    </span>
                    <span className="ideaweave-auth-modal__save-label">
                      {t("auth.save")}
                    </span>
                  </button>
                </div>
              </div>

              {/* Right: Plan */}
              <div className="ideaweave-auth-modal__field ideaweave-auth-modal__field--plan">
                <label>{t("auth.plan")}</label>
                {subLoading ? (
                  <span className="ideaweave-auth-modal__plan-loading">…</span>
                ) : userPlan === "pro" ? (
                  <span className="ideaweave-auth-modal__plan-badge ideaweave-auth-modal__plan-badge--pro">
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 14 }}
                    >
                      workspace_premium
                    </span>
                    Pro
                  </span>
                ) : (
                  <button
                    type="button"
                    className="ideaweave-auth-modal__upgrade-btn"
                    disabled={isUpgradeLoading}
                    onClick={handleUpgradeToPro}
                  >
                    {isUpgradeLoading ? (
                      <span
                        className="material-symbols-outlined ideaweave-auth-modal__spin"
                        style={{ fontSize: 14 }}
                      >
                        progress_activity
                      </span>
                    ) : (
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: 14 }}
                      >
                        workspace_premium
                      </span>
                    )}
                    {isUpgradeLoading
                      ? t("auth.upgradeRedirecting")
                      : t("auth.upgradeToPro")}
                  </button>
                )}
              </div>
            </div>

            {/* Language switcher */}
            <div className="ideaweave-auth-modal__field">
              <label>{t("auth.language")}</label>
              <div className="ideaweave-auth-modal__lang-switcher">
                <button
                  type="button"
                  className={`ideaweave-auth-modal__lang-btn ${
                    !isChinese ? "ideaweave-auth-modal__lang-btn--active" : ""
                  }`}
                  onClick={() => setLangCode("en")}
                >
                  English
                </button>
                <button
                  type="button"
                  className={`ideaweave-auth-modal__lang-btn ${
                    isChinese ? "ideaweave-auth-modal__lang-btn--active" : ""
                  }`}
                  onClick={() => setLangCode("zh-CN")}
                >
                  简体中文
                </button>
              </div>
            </div>

            {/* Microphone mode switcher */}
            <div className="ideaweave-auth-modal__field">
              <label>{t("auth.micMode")}</label>
              <div className="ideaweave-auth-modal__lang-switcher">
                <button
                  type="button"
                  className={`ideaweave-auth-modal__lang-btn ${
                    micMode === "smart"
                      ? "ideaweave-auth-modal__lang-btn--active"
                      : ""
                  }`}
                  onClick={() => setMicMode("smart")}
                >
                  {t("auth.micModeSmart")}
                </button>
                <button
                  type="button"
                  className={`ideaweave-auth-modal__lang-btn ${
                    micMode === "raw"
                      ? "ideaweave-auth-modal__lang-btn--active"
                      : ""
                  }`}
                  onClick={() => setMicMode("raw")}
                >
                  {t("auth.micModeRaw")}
                </button>
              </div>
            </div>

            {/* Sign out at bottom */}
            {showSignOutConfirm ? (
              <div className="ideaweave-auth-modal__signout-confirm">
                <span className="ideaweave-auth-modal__signout-confirm-text">
                  {t("auth.signOutConfirm")}
                </span>
                <div className="ideaweave-auth-modal__signout-confirm-actions">
                  <button
                    type="button"
                    className="ideaweave-auth-modal__signout-yes"
                    onClick={onSignOut}
                  >
                    {t("auth.yes")}
                  </button>
                  <button
                    type="button"
                    className="ideaweave-auth-modal__signout-no"
                    onClick={() => setShowSignOutConfirm(false)}
                  >
                    {t("auth.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="ideaweave-auth-modal__signout"
                onClick={() => setShowSignOutConfirm(true)}
              >
                {t("auth.signOut")}
              </button>
            )}
          </div>
        ) : (
          <div className="ideaweave-auth-modal__auth">
            <div className="ideaweave-auth-modal__auth-header">
              <h2>{t("auth.welcomeTitle")}</h2>
              <p>{t("auth.welcomeSubtitle")}</p>
            </div>

            {/* Language switcher for unauthenticated users */}
            <div className="ideaweave-auth-modal__lang-switcher ideaweave-auth-modal__lang-switcher--top">
              <button
                type="button"
                className={`ideaweave-auth-modal__lang-btn ${
                  !isChinese ? "ideaweave-auth-modal__lang-btn--active" : ""
                }`}
                onClick={() => setLangCode("en")}
              >
                English
              </button>
              <button
                type="button"
                className={`ideaweave-auth-modal__lang-btn ${
                  isChinese ? "ideaweave-auth-modal__lang-btn--active" : ""
                }`}
                onClick={() => setLangCode("zh-CN")}
              >
                简体中文
              </button>
            </div>

            <button
              type="button"
              className="ideaweave-auth-modal__google"
              onClick={onGoogleSignIn}
            >
              <span className="ideaweave-auth-modal__google-icon">G</span>
              {t("auth.continueGoogle")}
            </button>

            <div className="ideaweave-auth-modal__divider">
              <span>{t("auth.or")}</span>
            </div>

            {step === "otp" ? (
              /* ── Step 2: Enter 6-digit code ── */
              <div className="ideaweave-auth-modal__otp-step">
                <span
                  className="material-symbols-outlined ideaweave-auth-modal__otp-icon"
                  aria-hidden="true"
                >
                  mark_email_read
                </span>
                <p className="ideaweave-auth-modal__otp-title">
                  {t("auth.enterCode")}
                </p>
                <p className="ideaweave-auth-modal__otp-desc">
                  {t("auth.codeSentTo")} <strong>{email}</strong>
                </p>

                <form
                  className="ideaweave-auth-modal__form"
                  onSubmit={handleVerifyOtp}
                >
                  <input
                    className="ideaweave-auth-modal__otp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={otpCode}
                    onChange={(event) =>
                      setOtpCode(event.target.value.replace(/\D/g, ""))
                    }
                    placeholder="000000"
                    autoFocus
                    autoComplete="one-time-code"
                    required
                  />
                  <button
                    type="submit"
                    className="ideaweave-auth-modal__submit"
                    disabled={verifying || otpCode.length !== 6}
                  >
                    {verifying ? t("auth.verifying") : t("auth.verifySignIn")}
                  </button>
                </form>

                <button
                  type="button"
                  className="ideaweave-auth-modal__otp-resend"
                  onClick={async () => {
                    setSending(true);
                    setOtpCode("");
                    await onSendOtp(email);
                    setSending(false);
                  }}
                  disabled={sending}
                >
                  {sending ? t("auth.sending") : t("auth.resendCode")}
                </button>
                <button
                  type="button"
                  className="ideaweave-auth-modal__switch"
                  onClick={() => {
                    setStep("email");
                    setOtpCode("");
                  }}
                >
                  {t("auth.differentEmail")}
                </button>
              </div>
            ) : (
              /* ── Step 1: Enter email ── */
              <form
                className="ideaweave-auth-modal__form"
                onSubmit={handleSendOtp}
              >
                <div className="ideaweave-auth-modal__field">
                  <label htmlFor="iw-email">{t("auth.emailLabel")}</label>
                  <input
                    id="iw-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="ideaweave-auth-modal__submit"
                  disabled={sending || !email}
                >
                  {sending ? t("auth.sending") : t("auth.sendCode")}
                </button>
                <p className="ideaweave-auth-modal__hint">
                  {t("auth.noPasswordHint")}
                </p>
              </form>
            )}
          </div>
        )}

        {error ? (
          <div className="ideaweave-auth-modal__message">
            <span>{error}</span>
            <button type="button" onClick={onClearError}>
              {t("auth.dismiss")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
